import { useState } from 'react';
import { Box, Group, Loader, Text } from '@mantine/core';
import * as styles from './ActivityIndicator.css';
import { formatElapsedSeconds } from './elapsed-format';
import { useElapsedSeconds } from './useElapsedSeconds';

type ActivityIndicatorProps = {
  /** @desc What the agent is doing, from `deriveActivityLabel`. */
  label: string;
  /** @desc ISO start of the turn being waited on (`ClientEventGroup.tStart`). */
  startedAt?: string;
};

/**
 * @desc The single self-replacing status line a user sees while a turn runs: a spinner, what
 * the agent is doing now, and how long the turn has been going. It supersedes the plain
 * "Thinking…" placeholder outside developer mode and takes the place of the tool-trace rows —
 * the same information, at the altitude someone waiting for an answer actually wants it.
 *
 * The label is a prop, derived upstream from the turn's persisted events, so this component
 * holds no opinion about what is happening — only about how the change looks. The one piece of
 * state it does keep, the label being replaced, exists solely so the swap can cross-fade; it is
 * never read back as "what we were showing" and a reload rebuilds the line from the log alone.
 *
 * Mantine's Loader already respects reduced motion, so only the cross-fade needs guarding.
 */
export function ActivityIndicator({ label, startedAt }: ActivityIndicatorProps) {
  const elapsed = useElapsedSeconds(startedAt);

  // Adjusting state during render (the React-documented pattern) rather than in an effect: the
  // outgoing label has to be in the DOM in the very commit that shows the new one, or the fade
  // has nothing to fade from.
  const [shown, setShown] = useState(label);
  const [swap, setSwap] = useState<{ from: string; to: string } | null>(null);
  if (shown !== label) {
    setSwap({ from: shown, to: label });
    setShown(label);
  }
  const previous = swap?.to === label ? swap.from : null;

  return (
    <Group
      gap="xs"
      justify="center"
      py="sm"
      wrap="nowrap"
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <Loader size="sm" />
      <Box className={styles.labelSlot}>
        {previous && (
          <Text
            key={`${previous}→${label}`}
            aria-hidden="true"
            c="dimmed"
            fs="italic"
            size="sm"
            className={styles.outgoing}
          >
            {previous}
          </Text>
        )}
        <Text key={label} c="dimmed" fs="italic" size="sm" className={styles.incoming}>
          {label}
        </Text>
      </Box>
      {/* Hidden from the live region: the activity is worth announcing, a per-second tick is not. */}
      <Text aria-hidden="true" c="dimmed" fs="italic" size="sm">
        {formatElapsedSeconds(elapsed)}
      </Text>
    </Group>
  );
}
